import React, { useState, useEffect, useRef } from 'react';
import './ChatBot.css';
import { SendOutlined, CloseOutlined, RobotOutlined, ExpandOutlined, CompressOutlined, LeftOutlined, LoadingOutlined, CheckCircleFilled } from '@ant-design/icons';
import { apiUrl as apiBaseUrl } from '../../config';

const renderInlineText = (text) => {
    return String(text).split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={index}>{part.slice(2, -2)}</strong>;
        }

        return part;
    });
};

const parseTableRow = (line) => {
    return line
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((cell) => cell.trim());
};

const isTableSeparator = (line) => {
    return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
};

const isTableLine = (line) => {
    const trimmed = line.trim();
    return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.includes('|');
};

const isTableStart = (lines, index) => {
    return isTableLine(lines[index] || '') && isTableSeparator(lines[index + 1] || '');
};

const parseMessageBlocks = (text) => {
    const normalizedText = String(text)
        .replace(/\r\n/g, '\n')
        // Kod blokları ekrana basılmaz. Uç, grafik isteklerinde metnin sonuna
        // ```python:chart``` (matplotlib kodu) ve ```json:chart_data``` (ham
        // veri) bloklarını ekliyor; bunlar kullanıcıya değil çizime yönelik.
        // Gösterilirse sohbette yine kod/JSON yığını görünür.
        .replace(/```[\s\S]*?```/g, '')
        // Akış sırasında blok henüz kapanmamış olabilir; kapanışı beklemeden
        // gizlenir, yoksa yazılırken bir an kod/JSON görünür.
        .replace(/```[\s\S]*$/, '')
        .replace(/[ \t]+(#{1,4}\s+)/g, '\n$1');
    const lines = normalizedText.split('\n');
    const blocks = [];
    let index = 0;

    while (index < lines.length) {
        const line = lines[index];
        const trimmed = line.trim();

        if (!trimmed) {
            index += 1;
            continue;
        }

        const headingMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);
        if (headingMatch) {
            blocks.push({ type: 'heading', text: headingMatch[2] });
            index += 1;
            continue;
        }

        if (isTableStart(lines, index)) {
            const headers = parseTableRow(lines[index]);
            const rows = [];
            index += 2;

            while (index < lines.length && isTableLine(lines[index]) && !isTableSeparator(lines[index])) {
                rows.push(parseTableRow(lines[index]));
                index += 1;
            }

            blocks.push({ type: 'table', headers, rows });
            continue;
        }

        if (/^\s*[-*]\s+/.test(line)) {
            const items = [];

            while (index < lines.length && /^\s*[-*]\s+/.test(lines[index])) {
                items.push(lines[index].replace(/^\s*[-*]\s+/, ''));
                index += 1;
            }

            blocks.push({ type: 'list', items });
            continue;
        }

        const paragraphLines = [];
        while (
            index < lines.length &&
            lines[index].trim() &&
            !lines[index].trim().match(/^(#{1,4})\s+(.+)$/) &&
            !isTableStart(lines, index) &&
            !/^\s*[-*]\s+/.test(lines[index])
        ) {
            paragraphLines.push(lines[index].trim());
            index += 1;
        }

        blocks.push({ type: 'paragraph', text: paragraphLines.join('\n') });
    }

    return blocks;
};

const FormattedBotMessage = ({ text }) => {
    const blocks = parseMessageBlocks(text);

    return (
        <div className="formatted-message">
            {blocks.map((block, index) => {
                if (block.type === 'heading') {
                    return (
                        <h4 key={index} className="chatbot-markdown-heading">
                            {renderInlineText(block.text)}
                        </h4>
                    );
                }

                if (block.type === 'list') {
                    return (
                        <ul key={index} className="chatbot-markdown-list">
                            {block.items.map((item, itemIndex) => (
                                <li key={itemIndex}>{renderInlineText(item)}</li>
                            ))}
                        </ul>
                    );
                }

                if (block.type === 'table') {
                    return (
                        <div key={index} className="chatbot-table-wrapper">
                            <table className="chatbot-table">
                                <thead>
                                    <tr>
                                        {block.headers.map((header, headerIndex) => (
                                            <th key={headerIndex}>{renderInlineText(header)}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {block.rows.map((row, rowIndex) => (
                                        <tr key={rowIndex}>
                                            {block.headers.map((header, cellIndex) => (
                                                <td key={`${rowIndex}-${header}-${cellIndex}`}>
                                                    {renderInlineText(row[cellIndex] || '')}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    );
                }

                return (
                    <p key={index} className="chatbot-markdown-paragraph">
                        {renderInlineText(block.text)}
                    </p>
                );
            })}
        </div>
    );
};

/**
 * Yanıt gövdesini nesneye çevirir.
 *
 * Uç, isteğin türüne göre ya düz JSON ya da SSE akışı döndürüyor:
 * `event: tool_start` / `data: {...}` satır çiftleri, en sonda `event: done`.
 * Akış hâli JSON.parse ile çözülemediği için eski kod ham gövdeyi olduğu gibi
 * mesaj metni sanıyordu — ekranda görülen JSON yığını buydu.
 *
 * Akışta asıl yanıt son "done" olayının verisidir; o yoksa çözülebilen son
 * olay kullanılır.
 */
/** Olayları ayıran boş satır. */
const SSE_FRAME_SEPARATOR = /\r?\n\r?\n/;

/**
 * Tek bir SSE çerçevesini ({ name, data }) çözer. Veri satırı yoksa null döner.
 * Bir olayın verisi birden çok "data:" satırına bölünmüş olabilir.
 */
const parseEventFrame = (frame) => {
    let name = null;
    const dataLines = [];

    for (const line of frame.split(/\r?\n/)) {
        // ":" ile başlayan satır SSE yorumudur (sunucunun bağlantıyı canlı
        // tutmak için gönderdiği ping). Veri değil, atlanır.
        if (line.startsWith(':')) continue;
        if (line.startsWith('event:')) name = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
    }

    if (!dataLines.length) return null;

    const raw = dataLines.join('\n');
    try {
        return { name, data: JSON.parse(raw) };
    } catch {
        return { name, data: raw };
    }
};

const parseChatResponse = (body) => {
    if (!body) return null;

    if (/^\s*(event|data|:)/m.test(body)) {
        const events = [];
        for (const frame of body.split(SSE_FRAME_SEPARATOR)) {
            const parsed = parseEventFrame(frame);
            if (parsed) events.push(parsed);
        }

        for (let i = events.length - 1; i >= 0; i -= 1) {
            if (events[i].name === 'done') return events[i].data;
        }
        for (let i = events.length - 1; i >= 0; i -= 1) {
            if (events[i].data && typeof events[i].data === 'object') return events[i].data;
        }
    }

    try {
        return JSON.parse(body);
    } catch {
        // Son çare: gövde içindeki en son JSON nesnesini kurtarmayı dene.
        // Akış çerçevelemesi beklenmedik olsa bile (boş satır yerine tek satır
        // sonu, araya düşen log satırı) ham yığını ekrana basmaktan iyidir.
        const at = body.lastIndexOf('{"status_code"');
        if (at !== -1) {
            try {
                return JSON.parse(body.slice(at));
            } catch {
                /* kurtarılamadı */
            }
        }
        return body;
    }
};

/**
 * Langflow mesaj nesnesinden metni alır. Metin bazen doğrudan `text`
 * alanında, bazen `data.text` altında duruyor; hangisi olduğunu nesnenin
 * kendi `text_key` alanı söyler.
 */
const pickMessageText = (node) => {
    if (typeof node === 'string') return node;
    if (!node || typeof node !== 'object') return null;

    const key = typeof node.text_key === 'string' ? node.text_key : 'text';
    if (typeof node[key] === 'string') return node[key];
    if (node.data && typeof node.data[key] === 'string') return node.data[key];
    if (typeof node.message === 'string') return node.message;
    return null;
};

/** Yanıttaki olası konumlardan bot metnini çıkarır. */
const extractBotText = (payload, depth = 0) => {
    if (!payload || depth > 2) return null;
    if (typeof payload === 'string') return payload;

    const body = payload.data ?? payload;
    const output = (body?.outputs ?? payload?.outputs)?.[0]?.outputs?.[0];

    const text =
        pickMessageText(output?.results?.message) ??
        pickMessageText(output?.artifacts) ??
        pickMessageText(output?.messages?.[0]) ??
        pickMessageText(body?.result) ??
        pickMessageText(body) ??
        pickMessageText(payload);

    if (typeof text !== 'string' || !text.trim()) return null;

    // Metin yerine ham JSON/akış eline geçtiyse bir kez daha çözmeyi dene:
    // kullanıcıya hiçbir koşulda JSON yığını gösterilmesin.
    const trimmed = text.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('event:') || trimmed.startsWith('data:')) {
        const reparsed = parseChatResponse(trimmed);
        if (reparsed && typeof reparsed === 'object') {
            const inner = extractBotText(reparsed, depth + 1);
            if (inner) return inner;
        }
        return null;
    }

    return text;
};

const ChatBot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [message, setMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [messages, setMessages] = useState([
        { 
            id: 1, 
            text: "Merhaba! Ben Smart Asistan. Teknik konularda, operasyonel verimlilikte veya raporlamada size nasıl destek olabilirim?", 
            sender: 'bot' 
        }
    ]);
    const [sessionId, setSessionId] = useState(null);
    const [zoomImage, setZoomImage] = useState(null);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    const toggleChat = () => {
        setIsOpen(!isOpen);
    };

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    const handleSend = async (e) => {
        e.preventDefault();
        const currentMessage = message.trim();
        if (!currentMessage) return;

        const userMsg = { id: Date.now(), text: currentMessage, sender: 'user' };
        setMessages(prev => [...prev, userMsg]);
        setMessage('');
        setIsTyping(true);

        try {
            const payload = {
                data: {
                    input_value: currentMessage
                }
            };

            if (sessionId) {
                payload.data.session_id = sessionId;
            }

            const options = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            };

            const response = await fetch(`${apiBaseUrl}/runLangflow`, options);
            if (!response.ok) {
                throw new Error(`ChatBot request failed with status ${response.status}`);
            }

            // Cevap akış hâlinde geliyorsa "token" olayları balona canlı
            // yazılır; "tool_start"/"tool_end" olayları da akışın o anki
            // adımını gösteren bir kart olarak segments dizisine eklenir
            // (kart ve metin parçaları geldikleri sırayla iç içe durur).
            // Gövde bitmeden ekranda metin belirsin diye gövde topluca
            // değil parça parça okunur.
            const botId = Date.now() + 1;
            let streamedText = '';
            let data = null;

            const updateBubble = (updateSegments) => {
                setMessages((prev) => {
                    const index = prev.findIndex((m) => m.id === botId);
                    const current = index === -1
                        ? { id: botId, sender: 'bot', streaming: true, segments: [] }
                        : prev[index];
                    const updated = { ...current, segments: updateSegments(current.segments || []) };
                    if (index === -1) return [...prev, updated];
                    const next = [...prev];
                    next[index] = updated;
                    return next;
                });
            };

            const appendToBubble = (chunk) => {
                if (!chunk) return;
                streamedText += chunk;
                // "Yazıyor" noktaları akış boyunca durur; cevabın devam ettiğini
                // gösterir ve ancak "done" gelip balon nihai metinle
                // değiştirildiğinde (finally) kaybolur.
                updateBubble((segments) => {
                    const last = segments[segments.length - 1];
                    if (last?.type === 'text') {
                        return [...segments.slice(0, -1), { ...last, text: last.text + chunk }];
                    }
                    return [...segments, { type: 'text', text: chunk }];
                });
            };

            // tool_start/tool_end aynı çağrıyı "index" alanıyla eşler; kart
            // "done" olduğunda tik ikonuna döner.
            const startToolCard = (toolData) => {
                if (!toolData) return;
                const key = toolData.index ?? toolData.name;
                updateBubble((segments) => [
                    ...segments,
                    { type: 'tool', key, name: toolData.name, display: toolData.display || toolData.name, status: 'running' }
                ]);
            };

            const endToolCard = (toolData) => {
                if (!toolData) return;
                const key = toolData.index ?? toolData.name;
                updateBubble((segments) => segments.map((seg) => (
                    seg.type === 'tool' && seg.key === key && seg.status === 'running'
                        ? { ...seg, status: 'done' }
                        : seg
                )));
            };

            const handleFrame = (frame) => {
                const parsed = parseEventFrame(frame);
                if (!parsed) return;
                if (parsed.name === 'token') appendToBubble(parsed.data?.chunk);
                else if (parsed.name === 'tool_start') startToolCard(parsed.data);
                else if (parsed.name === 'tool_end') endToolCard(parsed.data);
                else if (parsed.name === 'done') data = parsed.data;
                // Diğer bilinmeyen olaylar (status dahil) yok sayılır.
            };

            if (response.body?.getReader) {
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });

                    let match;
                    while ((match = SSE_FRAME_SEPARATOR.exec(buffer)) !== null) {
                        handleFrame(buffer.slice(0, match.index));
                        buffer = buffer.slice(match.index + match[0].length);
                    }
                }

                buffer += decoder.decode();
                // Akış son çerçeveyi boş satırla kapatmamış olabilir.
                if (buffer.trim()) handleFrame(buffer);

                // Akış hiç olay vermediyse gövde düz JSON olabilir.
                if (!data && !streamedText) data = parseChatResponse(buffer);
            } else {
                data = parseChatResponse(await response.text());
            }

            // Akan metin ön izlemedir; nihai ve temiz hâli "done" olayındadır.
            let botText =
                extractBotText(data) ||
                streamedText ||
                "Üzgünüm, şu an cevap veremiyorum. Lütfen tekrar deneyin.";

            if (typeof botText !== 'string') {
                botText = JSON.stringify(botText);
            }

            // Update session ID if present in the response
            if (data?.session_id) {
                setSessionId(data.session_id);
            } else if (data?.data?.session_id) {
                setSessionId(data.data.session_id);
            }

            // Extract charts (base64 images) from the response if present
            let charts = [];
            if (Array.isArray(data?.charts)) {
                charts = data.charts;
            } else if (Array.isArray(data?.data?.charts)) {
                charts = data.data.charts;
            } else if (Array.isArray(data?.data?.outputs?.[0]?.outputs?.[0]?.results?.charts)) {
                charts = data.data.outputs[0].outputs[0].results.charts;
            }
            charts = charts.filter((c) => c && c.image);

            // Akan balon varsa yerine nihai metin geçer, yoksa yeni balon açılır.
            const botMsg = { id: botId, text: botText, sender: 'bot', charts };
            setMessages((prev) => {
                const index = prev.findIndex((m) => m.id === botId);
                if (index === -1) return [...prev, botMsg];
                const next = [...prev];
                next[index] = botMsg;
                return next;
            });
        } catch (err) {
            console.error("ChatBot Error:", err);
            const errorMsg = { 
                id: Date.now() + 1, 
                text: "Bağlantı hatası oluştu. Lütfen internet bağlantınızı kontrol edin.", 
                sender: 'bot' 
            };
            setMessages(prev => [...prev, errorMsg]);
        } finally {
            setIsTyping(false);
        }
    };

    return (
        <div className="chatbot-container">
            {/* Right-edge docked tab */}
            <div
                className={`chatbot-dock-tab ${isOpen ? '' : 'visible'}`}
                onClick={() => setIsOpen(true)}
                title="ChatBot'u aç"
            >
                <LeftOutlined className="chatbot-dock-tab-arrow" />
                <span className="chatbot-dock-tab-text">ChatBot</span>
            </div>

            {/* Gemini-Inspired Wide Chat Window */}
            <div className={`chatbot-window ${isOpen ? 'open' : ''} ${isFullScreen ? 'fullscreen' : ''}`}>
                <div className="chatbot-header">
                    <div className="chatbot-header-info">
                        <div className="chatbot-avatar">
                            <RobotOutlined style={{ fontSize: '22px' }} />
                        </div>
                        <div className="chatbot-header-text">
                            <h3>Smart Asistan</h3>
                            <p>Çevrimiçi</p>
                        </div>
                    </div>
                    <div className="chatbot-header-actions">
                        <div className="action-btn" onClick={toggleFullScreen} title={isFullScreen ? "Küçült" : "Tam Ekran"}>
                            {isFullScreen ? <CompressOutlined style={{ fontSize: '20px' }} /> : <ExpandOutlined style={{ fontSize: '20px' }} />}
                        </div>
                        <div className="action-btn close-btn" onClick={toggleChat} title="Kapat">
                            <CloseOutlined style={{ fontSize: '20px' }} />
                        </div>
                    </div>
                </div>

                <div className="chatbot-messages">
                    {messages.map((msg) => (
                        <div key={msg.id} className={`message ${msg.sender}`}>
                            {msg.sender === 'bot' && (
                                <div className="bot-icon-small">
                                    <RobotOutlined />
                                </div>
                            )}
                            <div className="message-content">
                                {msg.sender === 'bot' ? (
                                    msg.segments ? (
                                        <div className="chatbot-stream-segments">
                                            {msg.segments.map((seg, segIndex) => (
                                                seg.type === 'tool' ? (
                                                    <div
                                                        key={seg.key ?? segIndex}
                                                        className={`chatbot-tool-card ${seg.status === 'done' ? 'done' : 'running'}`}
                                                    >
                                                        {seg.status === 'done' ? <CheckCircleFilled /> : <LoadingOutlined spin />}
                                                        <span>{seg.display}</span>
                                                    </div>
                                                ) : (
                                                    <FormattedBotMessage key={segIndex} text={seg.text} />
                                                )
                                            ))}
                                        </div>
                                    ) : (
                                        <FormattedBotMessage text={msg.text} />
                                    )
                                ) : msg.text}
                                {msg.sender === 'bot' && msg.charts?.length > 0 && (
                                    <div className="chatbot-charts">
                                        {msg.charts.map((chart, chartIndex) => (
                                            <div key={chart.id || chartIndex} className="chatbot-chart">
                                                {chart.title && (
                                                    <div className="chatbot-chart-title">{chart.title}</div>
                                                )}
                                                <img
                                                    src={chart.image}
                                                    alt={chart.title || `chart-${chartIndex}`}
                                                    className="chatbot-chart-image"
                                                    onClick={() => setZoomImage(chart.image)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {isTyping && (
                        <div className="message bot">
                            <div className="bot-icon-small">
                                <RobotOutlined />
                            </div>
                            <div className="typing">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <div className="chatbot-input-container">
                    <form className="chatbot-input-area" onSubmit={handleSend}>
                        <input 
                            type="text" 
                            placeholder="Bir şeyler sorun veya komut yazın..." 
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                        />
                        <button type="submit" className="chatbot-send-btn">
                            <SendOutlined style={{ fontSize: '20px' }} />
                        </button>
                    </form>
                </div>
            </div>

            {zoomImage && (
                <div className="chatbot-image-overlay" onClick={() => setZoomImage(null)}>
                    <img src={zoomImage} alt="chart" className="chatbot-image-overlay-img" />
                </div>
            )}
        </div>
    );
};

export default ChatBot;
